import React from "react";
import { Link } from "react-router-dom";
import DiscoveryLayout, {
  Faq,
  SourceLink,
} from "../../components/discovery/DiscoveryLayout";
import subscriptionLevels from "../../data/featuresBySubscriptionLevel.json";

const plans = Object.values(subscriptionLevels);
const firstPlanWith = (feature) => plans.find((plan) => plan.features[feature]).tier;

const agencies = [
  {
    id: "american-sweepstakes",
    name: "American Sweepstakes & Promotion Co.",
    services: "Official rules, filings and bonds, winner administration, prize fulfillment",
    focus: "E-commerce giveaways and broader brand promotions",
    source: "https://american-sweeps.com/",
    sourceLabel: "American Sweepstakes services",
    description:
      "American Sweepstakes explicitly offers e-commerce giveaway services alongside official rules, compliance support, winner administration and prize fulfillment. Its published services include affidavit verification and tax reporting. That makes it a useful company to contact when your Shopify campaign needs someone responsible for administration beyond collecting entries.",
    question:
      "Ask how purchase entries and your chosen free-entry method will be reconciled, who approves the final draw file, and whether prize delivery and winner paperwork are included in the quote.",
  },
  {
    id: "marden-kane",
    name: "Marden-Kane",
    services: "Rules, registration and bonding, AMOE management, winner clearances, fulfillment",
    focus: "Sweepstakes, judged contests and instant-win programs",
    source: "https://www.mardenkane.com/our-services/",
    sourceLabel: "Marden-Kane service list",
    description:
      "Marden-Kane lists AMOE management, winner clearances, travel coordination and hosted promotion sites. It also supports judging workflows and public voting for contests. Its services page distinguishes its practical compliance support from legal advice, a useful distinction when you are assigning responsibilities between an administrator and your own counsel.",
    question:
      "Ask which services apply to your chance-based sweepstakes, whether you can retain your Shopify storefront, and how customer inquiries and free entries will be handled.",
  },
  {
    id: "don-jagoda",
    name: "Don Jagoda Associates (DJA)",
    services: "Sweepstakes administration; confirm the contracted service scope",
    focus: "Brand promotions, including documented Coca-Cola campaigns",
    source: "https://www.coca-cola.com/us/en/legal/essence-festival-of-culture",
    sourceLabel: "Coca-Cola's 2026 Essence Festival official rules",
    description:
      "Don Jagoda Associates is named as administrator in Coca-Cola's 2026 Essence Festival of Culture sweepstakes rules. This is a concrete administration example, not an endorsement or an open promotion to enter. DJA's company profile also lists sweepstakes, contests, instant-win games and consumer rewards among its specialties.",
    question:
      "For a Shopify merchandise campaign, request an explicit scope for entry intake, winner handling and fulfillment. A company's role in another promotion does not establish what your engagement includes.",
    extraSource: "https://www.linkedin.com/company/don-jagoda-associates",
    extraSourceLabel: "DJA's company profile",
  },
  {
    id: "realtime-media",
    name: "Realtime Media",
    services: "Rules and compliance, winner outreach, prize sourcing and fulfillment",
    focus: "Standalone administration or digital promotion development",
    source: "https://www.rtm.com/",
    sourceLabel: "Realtime Media capabilities",
    description:
      "Realtime Media separates its PromoAdmin administration services from its PromoPick entry-collection technology. It advertises both individual services and end-to-end promotion delivery, with options for branded microsites, social entries and SMS. That separation gives merchants a concrete starting point for discussing whether an existing commerce workflow can remain in place.",
    question:
      "Ask for administration-only and full-development scopes separately, including the entry format each requires and the responsibilities for winner outreach and prize shipping.",
  },
  {
    id: "us-sweepstakes",
    name: "US Sweepstakes & Fulfillment",
    services: "Campaign planning, rules and filings, winner verification, prize logistics",
    focus: "Promotion execution and fulfillment for brands and agencies",
    source: "https://ussweeps.com/what-we-do/",
    sourceLabel: "US Sweepstakes management services",
    description:
      "US Sweepstakes & Fulfillment describes a service spanning campaign planning, official rules, registrations, winner verification and prize delivery. Its fulfillment offering includes cash, gift cards, merchandise and travel, as well as winner support and tax reporting. Those operational details matter when the prize is more complicated than a single shipped product.",
    question:
      "Provide the prize list and delivery destinations when requesting a quote. Ask which procurement, shipping, winner-support and tax-reporting costs are included and which are separate.",
  },
  {
    id: "cfa-promo",
    name: "CFA Promo / Cohen-Friedberg Associates",
    services: "Counsel-coordinated rules, filings, drawings, winner validation, fulfillment",
    focus: "Third-party operation of sweepstakes, contests and instant wins",
    source: "https://www.cfapromo.com/sweepstakes-administration",
    sourceLabel: "CFA Promo administration services and package pricing",
    pricing: "Administration packages from $2,000; request a scoped quote",
    description:
      "CFA Promo describes coordination with sponsor counsel, entry-integrity controls, documented drawings and fulfillment. It publishes administration packages starting at $2,000, with final pricing dependent on the promotion. That is an agency package starting point, not a monthly app subscription or a guarantee that every service is included.",
    question:
      "Ask for the package deliverables, exclusions, filing costs and launch dependencies in writing. Compare its quote against the same prize, audience, entry methods and campaign dates used in other proposals.",
  },
  {
    id: "merkle-promotions",
    name: "Merkle Promotions",
    services: "Promotion technology, legal compliance, analytics, prize procurement and fulfillment",
    focus: "Digital promotions, gamification and loyalty experiences",
    source: "https://www.promotions.com/",
    sourceLabel: "Merkle Promotions capabilities and former names",
    description:
      "Merkle Promotions combines strategy, creative work, promotion technology and administration. Its site includes purchase-validation and receipt-upload experiences alongside games, legal compliance and fulfillment. The company identifies ePrize and HelloWorld as earlier names; they are included here under Merkle, rather than counted as additional agencies.",
    question:
      "If your campaign spans retail receipts, branded games and an online store, ask which channels share an entry record and how data moves between the promotion platform and your commerce systems.",
  },
  {
    id: "ebbo",
    name: "ebbo",
    services: "Sweepstakes experiences, legal support, customer service and prize management",
    focus: "Promotions connected to loyalty and customer acquisition",
    source: "https://www.ebbo.com/solutions/promotions/sweepstakes/",
    sourceLabel: "ebbo sweepstakes management",
    description:
      "ebbo positions sweepstakes within a broader loyalty strategy, including member acquisition, opt-ins and participation across channels. Its offering lists creative, development, analytics, legal support and prize management. Its rebrand brought the Clarus Commerce and PrizeLogic businesses under the ebbo name, so PrizeLogic is not counted separately in this guide.",
    question:
      "Ask whether you need a standalone campaign or a wider loyalty engagement, what customer data is collected, and how an existing store and membership program fit the proposed scope.",
    extraSource: "https://www.ebbo.com/insights/blog/say-hello-to-ebbo-your-all-in-one-loyalty-partner/",
    extraSourceLabel: "ebbo's rebrand announcement",
  },
  {
    id: "ventura-associates",
    name: "Ventura Associates",
    services: "Brand activation, digital promotions, compliance support and international marketing",
    focus: "Experiential and cooperative promotions alongside sweepstakes",
    source: "https://sweepspros.com/",
    sourceLabel: "Ventura Associates service overview",
    description:
      "Ventura Associates lists brand activation, digital technology, social campaigns, experiential marketing and compliance support. It also offers cooperative sweepstakes, a distinct model to investigate when a shared promotion might fit your audience. Its broader marketing services should be evaluated against what your specific merchandise giveaway needs.",
    question:
      "Ask whether the proposed campaign is exclusive to your brand or shared, who owns the entrant relationship, and which storefront, administration and fulfillment responsibilities remain with your team.",
  },
  {
    id: "promosis",
    name: "Promosis",
    services: "Rules planning, registration support, entry monitoring, winner verification, fulfillment coordination",
    focus: "Multi-channel campaigns and operational promotion oversight",
    source: "https://www.promosis.com/sweepstakes-administration",
    sourceLabel: "Promosis administration services",
    description:
      "Promosis describes support for official rules, registration, winner verification and prize coordination. It also explicitly covers entry monitoring, including duplicate submissions, automated activity and suspicious referral behavior. Its social-promotion services include moderation and platform-specific considerations, which can matter when your campaign extends beyond the store.",
    question:
      "Ask how flagged entries are reviewed, who makes eligibility decisions, and what records your team receives. Clarify how purchase, referral and free-entry records will be reconciled before selection.",
  },
];

const sections = [
  { id: "choose-your-support", label: "Software or an administrator?" },
  { id: "agency-comparison", label: "Compare 10 companies" },
  ...agencies.map(({ id, name }) => ({ id, label: name })),
  { id: "clean-sweeps-workflow", label: "Where Clean Sweeps fits" },
  { id: "agency-brief", label: "Prepare your agency brief" },
  { id: "frequently-asked-questions", label: "Frequently asked questions" },
];

export default function SweepstakesAgenciesGuide() {
  return (
    <DiscoveryLayout
      path="/guides/sweepstakes-administration-companies"
      title="10 sweepstakes administration companies to compare"
      description="Compare American Sweepstakes, Marden-Kane, DJA and other administrators, and learn how Clean Sweeps handles purchase entries for Shopify merchants."
      eyebrow="Sweepstakes planning"
      sections={sections}
    >
      <section id="choose-your-support">
        <h2>Start with the job you need done</h2>
        <p>
          For a Shopify merchandise giveaway, Clean Sweeps gives you purchase-entry
          automation, product bonuses, storefront displays and entrant management
          in one app. A professional sweepstakes administrator can take on a
          different set of responsibilities: promotion planning, rules coordination,
          filings, winner verification and prize logistics. Your campaign may benefit
          from both.
        </p>
        <p>
          These ten companies were selected for their published administration or
          promotion services. This is a practical shortlist, not a ranking by search
          volume or popularity. The service summaries describe the linked sources;
          request a written proposal to confirm what each company will do for you.
        </p>
      </section>

      <section id="agency-comparison">
        <h2>Compare the services before comparing the price</h2>
        <p>
          An agency proposal may cover an entire campaign, while Clean Sweeps plans
          cover app access with monthly processed-order limits. Compare each quote's
          deliverables and billing unit. Request a quote where a usable package price
          is not specified in the sources reviewed; this does not mean the company
          lacks a public price elsewhere.
        </p>
        <div
          className="discovery-table-scroll"
          role="region"
          aria-label="Sweepstakes administration company comparison"
          tabIndex={0}
        >
          <table>
            <caption>Published services and pricing to confirm with each administrator</caption>
            <thead>
              <tr>
                <th scope="col">Company</th>
                <th scope="col">Published services</th>
                <th scope="col">Scope to explore</th>
                <th scope="col">Pricing</th>
              </tr>
            </thead>
            <tbody>
              {agencies.map((agency) => (
                <tr key={agency.id}>
                  <th scope="row"><a href={`#${agency.id}`}>{agency.name}</a></th>
                  <td>{agency.services}</td>
                  <td>{agency.focus}</td>
                  <td>{agency.pricing || "Request a quote"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {agencies.map((agency) => (
        <section id={agency.id} key={agency.id}>
          <h2>{agency.name}</h2>
          <p>{agency.description}</p>
          <p><strong>What to ask:</strong> {agency.question}</p>
          <p>
            <SourceLink href={agency.source}>{agency.sourceLabel}</SourceLink>
            {agency.extraSource && (
              <>
                {" · "}
                <SourceLink href={agency.extraSource}>{agency.extraSourceLabel}</SourceLink>
              </>
            )}
          </p>
        </section>
      ))}

      <section id="clean-sweeps-workflow">
        <h2>Use Clean Sweeps for the Shopify purchase-entry workflow</h2>
        <p>
          Clean Sweeps is a strong fit when your daily work is configuring entries
          per dollar, showing entry opportunities on products and managing the
          resulting entrant records. Start with your campaign's approved entry
          rules, then configure the app and test the customer experience before
          promoting the giveaway.
        </p>
        <ul>
          <li>
            Set the entry rate, eligible products and product bonuses. Match every
            advertised offer to the campaign configuration.
          </li>
          <li>
            Place the product entry widget; add cart and guest lookup displays on{" "}
            {firstPlanWith("cartWidget")} and above so shoppers can understand their entries.
          </li>
          <li>
            Add referral rewards and memberships on {firstPlanWith("referralCodes")}
            {" "}and above when those mechanics are part of your approved campaign.
          </li>
          <li>
            On {firstPlanWith("amoe")}, use the no-purchase AMOE request form with
            pending review and approval. Agree on the free-entry process and review
            criteria with your administrator or counsel before launch.
          </li>
          <li>
            If an administrator conducts the drawing, agree on the export format,
            cutoff and reconciliation process in advance. This is a proposed working
            arrangement, not a claim of an integration with any listed agency.
          </li>
        </ul>
        <p>
          You can use Clean Sweeps as your software without hiring one of these
          agencies when your team has separately arranged the necessary legal and
          operational work. App setup and an AMOE form do not by themselves establish
          that a promotion is legally ready. Have qualified counsel review your
          specific campaign. See the <Link to="/setup">Clean Sweeps setup guide</Link>
          {" "}and <Link to="/#pricing">current plans and feature availability</Link>.
        </p>
      </section>

      <section id="agency-brief">
        <h2>Send every agency the same brief</h2>
        <p>
          Prepare one document with campaign dates, eligible locations, prize values,
          expected order volume and entry methods. Include the purchase-entry rate,
          any multipliers, referral mechanics and intended AMOE. This helps proposals
          address the same campaign instead of different assumptions.
        </p>
        <ul>
          <li>Who approves official rules, advertisements and later campaign changes?</li>
          <li>Who handles any required registrations, bonds and associated costs?</li>
          <li>Who receives free entries and resolves duplicate or disputed records?</li>
          <li>Who performs the drawing, verifies eligibility and contacts alternates?</li>
          <li>Who owns prize delivery, tax documentation and entrant support?</li>
          <li>What records are returned, how are they transferred, and when are they deleted?</li>
        </ul>
        <p>
          Keep the selected responsibilities in the contract. Clean Sweeps setup
          support can help with the app configuration and standard widgets; it does
          not silently transfer legal administration or fulfillment duties from your
          business to the app team.
        </p>
      </section>

      <Faq items={[
          {
            question: "Is Clean Sweeps an alternative to American Sweepstakes?",
            answer: "Clean Sweeps is an option for Shopify purchase-entry automation and storefront displays. American Sweepstakes offers professional administration services. Decide which responsibilities you need covered: the app and an administrator can serve different parts of the same campaign, subject to an agreed process.",
          },
          {
            question: "Does an AMOE form make my sweepstakes legal?",
            answer: "No. A form is one operational tool. Your campaign still needs rules and procedures appropriate to its prize, audience, locations and entry mechanics. Have qualified counsel review the specific promotion and make sure your configured workflow follows those rules.",
          },
          {
            question: "Are these the ten most searched sweepstakes agencies?",
            answer: "No search-volume ranking is claimed. This guide selects ten companies with documented services relevant to merchants planning promotions. Compare the fit and written scope for your campaign instead of treating list order as a recommendation score.",
          },
          {
            question: "Can I keep my Shopify store while hiring an administrator?",
            answer: "Ask the administrator to scope that arrangement before signing. Confirm whether it accepts your entry exports, how free entries are reconciled, and which system supplies the final eligible drawing records. This guide does not promise a direct Clean Sweeps integration with any agency.",
          },
      ]} />
    </DiscoveryLayout>
  );
}
